// SPDX-License-Identifier: Apache-2.0

// tether-brain v2 — ingest dispatcher.
//
// Forge-stream scope: dispatcher + md/txt parsers ONLY. Other parsers will be
// ported in their own streams (see the future-stream map below). Each stub
// throws `not yet implemented in v1` with a pointer to where the real parser
// will land, so a misrouted drop in the SPA fails loud and consistently.
//
// Frontmatter handling: `.md` files may begin with a `---` block. We parse
// that into `VaultFrontmatter` (yaml-lite — string/number/string-array values
// only; deeper nesting passes through verbatim as `string`). The body excludes
// the frontmatter block so it round-trips cleanly through the editor.

import type { IngestKind, IngestResult, VaultFrontmatter } from './schema';
import { isTier } from './schema';
import {
  parseDocx,
  parsePdf,
  parseCsv,
  parseXlsx,
  parseUdf,
} from '@tether/ingest';

// ─── extension routing ─────────────────────────────────────────────────────

const FORGE_PARSERS = new Set(['.md', '.txt']);

/** Heavy parsers that live in `@tether/ingest`. The dispatcher delegates by
 *  extension. Spread .xls into .xlsx — SheetJS handles both with the same
 *  reader — so the spreadsheet path covers legacy Excel files too. */
const HEAVY_PARSERS = new Set(['.docx', '.pdf', '.csv', '.xlsx', '.xls', '.udf']);

const ALL_EXTS = new Set<string>([
  ...FORGE_PARSERS,
  ...HEAVY_PARSERS,
]);

export const INGEST_ACCEPT = '.md,.txt,.docx,.pdf,.xlsx,.xls,.csv,.udf';

export function extOf(name: string): string {
  const i = name.lastIndexOf('.');
  return i < 0 ? '' : name.slice(i).toLowerCase();
}

export function isIngestable(file: File): boolean {
  return ALL_EXTS.has(extOf(file.name));
}

function baseTitle(name: string): string {
  const ext = extOf(name);
  const stem = ext ? name.slice(0, -ext.length) : name;
  return `${stem.trim() || 'imported'}.md`;
}

// ─── md/txt parsers (forge-stream owned) ───────────────────────────────────

function asParagraphs(raw: string): string {
  return raw
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .map((l) => l.replace(/[\t ]+$/g, ''))
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

const FRONTMATTER_RE = /^---\r?\n([\s\S]*?)\r?\n---\r?\n?/;

/** Tiny YAML subset — handles `key: value`, `key: [a, b, c]`, and
 *  `key:\n  - a\n  - b`. Unknown shapes pass through as raw strings. */
export function parseFrontmatter(raw: string): {
  front: VaultFrontmatter;
  body: string;
} {
  const m = raw.match(FRONTMATTER_RE);
  if (!m) return { front: {}, body: raw };
  const block = m[1] ?? '';
  const body = raw.slice(m[0].length);
  const front: VaultFrontmatter = {};

  const lines = block.split(/\r?\n/);
  let i = 0;
  while (i < lines.length) {
    const line = lines[i] ?? '';
    if (!line.trim()) {
      i++;
      continue;
    }
    const kv = line.match(/^([A-Za-z_][\w-]*)\s*:\s*(.*)$/);
    if (!kv) {
      i++;
      continue;
    }
    const key = kv[1];
    const restRaw = kv[2] ?? '';
    const rest = restRaw.trim();
    if (key === undefined) {
      i++;
      continue;
    }

    if (rest === '') {
      // Block-style list: collect indented `  - item` lines that follow.
      const items: string[] = [];
      let j = i + 1;
      while (j < lines.length) {
        const next = lines[j] ?? '';
        const lm = next.match(/^\s+-\s+(.*)$/);
        if (!lm) break;
        items.push(stripQuotes((lm[1] ?? '').trim()));
        j++;
      }
      if (items.length > 0) {
        front[key] = items;
        i = j;
        continue;
      }
      front[key] = '';
      i++;
      continue;
    }

    // Inline list `[a, b, c]`.
    if (rest.startsWith('[') && rest.endsWith(']')) {
      const inner = rest.slice(1, -1);
      const items = inner
        .split(',')
        .map((p) => stripQuotes(p.trim()))
        .filter(Boolean);
      front[key] = items;
      i++;
      continue;
    }

    front[key] = coerceScalar(stripQuotes(rest));
    i++;
  }

  // Validate the typed fields we care about; keep extras as-is.
  const out: VaultFrontmatter = {};
  for (const [k, v] of Object.entries(front)) {
    if (k === 'tags' && Array.isArray(v)) {
      out.tags = v.map(String);
    } else if (k === 'sensitivity' && isTier(v)) {
      out.sensitivity = v;
    } else if ((k === 'title' || k === 'permalink' || k === 'created' || k === 'slot') &&
               typeof v === 'string') {
      out[k] = v;
    } else if (k === 'type' && typeof v === 'string') {
      out.type = v as VaultFrontmatter['type'];
    } else {
      out[k] = v;
    }
  }
  return { front: out, body };
}

function stripQuotes(s: string): string {
  if (
    (s.startsWith('"') && s.endsWith('"')) ||
    (s.startsWith("'") && s.endsWith("'"))
  ) {
    return s.slice(1, -1);
  }
  return s;
}

function coerceScalar(s: string): string | number | boolean {
  if (s === 'true') return true;
  if (s === 'false') return false;
  if (s !== '' && /^-?\d+(?:\.\d+)?$/.test(s)) {
    const n = Number(s);
    if (!Number.isNaN(n)) return n;
  }
  return s;
}

async function parseTextLike(file: File): Promise<string> {
  return await file.text();
}

// ─── public dispatcher ─────────────────────────────────────────────────────

export async function parseFile(file: File): Promise<IngestResult | null> {
  const ext = extOf(file.name);
  if (!ALL_EXTS.has(ext)) return null;

  const title = baseTitle(file.name);

  if (ext === '.md') {
    const raw = await parseTextLike(file);
    const { front, body } = parseFrontmatter(raw);
    return {
      title,
      content: asParagraphs(body),
      front,
      kind: 'md',
    };
  }

  if (ext === '.txt') {
    const raw = await parseTextLike(file);
    return {
      title,
      content: asParagraphs(raw),
      front: {},
      kind: 'txt',
    };
  }

  // Heavy formats live in @tether/ingest. Each parser is responsible for
  // catching its own errors and returning a corrupt-result placeholder, so
  // this branch is straight delegation — no try/catch needed.
  if (ext === '.docx') return parseDocx(file);
  if (ext === '.pdf') return parsePdf(file);
  if (ext === '.csv') return parseCsv(file);
  if (ext === '.xlsx' || ext === '.xls') return parseXlsx(file);
  if (ext === '.udf') return parseUdf(file);

  return null;
}

export interface IngestBatch {
  results: IngestResult[];
  skipped: File[];
  /** Files that matched a known extension but threw during parse — the
   *  dispatcher's "not yet implemented" stubs land here, plus any genuine
   *  parse failures. */
  failed: Array<{ file: File; error: Error }>;
}

export async function parseFiles(files: File[] | FileList): Promise<IngestBatch> {
  const list = Array.from(files);
  const results: IngestResult[] = [];
  const skipped: File[] = [];
  const failed: Array<{ file: File; error: Error }> = [];
  for (const file of list) {
    if (!isIngestable(file)) {
      skipped.push(file);
      continue;
    }
    try {
      const res = await parseFile(file);
      if (res) results.push(res);
      else skipped.push(file);
    } catch (err) {
      failed.push({
        file,
        error: err instanceof Error ? err : new Error(String(err)),
      });
    }
  }
  return { results, skipped, failed };
}

// ─── reverse: VaultDoc → string with frontmatter ────────────────────────────

/** Render a `VaultDoc` back to a markdown file body with frontmatter. Used by
 *  the MCP `save_to_vault` round-trip and by FSAccess writes. */
export function renderWithFrontmatter(
  front: VaultFrontmatter,
  body: string,
): string {
  const keys = Object.keys(front).filter((k) => front[k] !== undefined);
  if (keys.length === 0) return body.endsWith('\n') ? body : `${body}\n`;
  const lines: string[] = ['---'];
  for (const k of keys) {
    const v = front[k];
    if (Array.isArray(v)) {
      lines.push(`${k}: [${v.map((x) => formatScalar(x)).join(', ')}]`);
    } else {
      lines.push(`${k}: ${formatScalar(v)}`);
    }
  }
  lines.push('---', '');
  const head = lines.join('\n');
  return `${head}${body}${body.endsWith('\n') ? '' : '\n'}`;
}

function formatScalar(v: unknown): string {
  if (typeof v === 'string') {
    if (v === '' || /[:#\[\]\{\}]/.test(v) || /^\s|\s$/.test(v)) {
      return JSON.stringify(v);
    }
    return v;
  }
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return JSON.stringify(v);
}

// Re-export the kind union for callers that don't want to import schema directly.
export type { IngestKind, IngestResult };
